<template>
  <v-card>
    <v-card-title>
      {{$t('dash.add_a_video')}}
    </v-card-title>
    <v-card-text>
      <dashboard
        :uppy="uppy"
        :props="{theme: $vuetify.theme.dark ? 'dark' : 'light', ...options, height: 200}"
        :plugins="['Url']"
      />
      <v-text-field
        class="mb-5 mt-5"
        solo
        v-model="video.title"
        hide-details
        :placeholder="$t('dashboard.video_title')"
      ></v-text-field>
      <!-- <v-text-field
        solo
        v-model="video.description"
        :placeholder="$t('dashboard.video_description')"
      ></v-text-field> -->
      <div class="mb-3" v-if="fileCount > 0">
        {{$t('dash.video_duration')}} {{ secondsToPretty(video.duration) }}
      </div>
    </v-card-text>
    <v-card-actions>
      <v-btn
        @click="closeDialog"
      >
        {{$t('cancel')}}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        @click="upload()"
        color="primary"
        :disabled="uploadDisabled"
        :loading="uploading"
      >
        {{$t('dashboard.upload')}}
        <template v-slot:loader>
          <span>{{$t('dashboard.uploading')}}</span>
        </template>
      </v-btn>
    </v-card-actions>
    <no-upload-modal
      v-model="noSpaceDialog"
    ></no-upload-modal>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations }  from 'vuex'
import { Dashboard } from '@uppy/vue'
import Url from '@uppy/url'
import time from '@/utils/player/time'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import '@uppy/url/dist/style.css'
import Finnish from '@uppy/locales/lib/fi_FI'
import { v4 as uuidv4 } from 'uuid';

import Uppy from '@uppy/core'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import NoUploadModal from '@/components/video/NoUploadModal.vue'

export default {
  props: ['options', 'yt_download_url', 'video_title'],
  mixins: [time],
  components: {
    Dashboard,
    NoUploadModal
  },
  created() {
    this.uppy.on('file-added', this.fileAdded)
    this.uppy.on('file-removed', this.fileRemoved)
    this.uppy.on('upload-success', this.uploaded)
    this.uppy.on('upload', this.uploadStarts)
    this.uppy.on('upload-error', this.uppyError)
    this.uppy.on('error', this.error)
    this.uppy.on('complete', this.complete)

    if(this.yt_download_url) {
      this.uppy.getPlugin('Url').addFile(this.yt_download_url)
      this.video.title = this.video_title
      this.isRemote = true
    }
  },
  data: () => ({
    video: {
      title: '',
      description: '',
      duration: 0
    },
    fileCount: 0,
    uploading: false,
    noSpaceDialog: false,
    isRemote: false,
    remoteUrl: ''
  }),
  computed: {
    ...mapGetters('user', [
      'currentTeamId',
      'currentTeam'
    ]),
    ...mapGetters({
      'amountUploaded': 'videos/uploaded'
    }),
    uppy() {
      // Do not try this at home please
      const local = window.location.href.includes('localhost')
      const dev = window.location.href.includes('dev.')

      let apiBase;
      if(local) {
        apiBase = 'http://localhost:4040'
      } else if(dev) {
        apiBase = 'https://tiimi-development.herokuapp.com'
      } else {
        apiBase = 'https://api.epesis3.com'
      }

      return new Uppy({
        ...this.options,
        locale: window.localStorage.getItem('tiimi-lang') == 'fi' ? Finnish : '',
        onBeforeFileAdded: (file) => {
          const name = uuidv4()
          Object.defineProperty(file.data, 'name', {
            writable: true,
            value: name
          });
          return { ...file, name, meta: { ...file.meta, name } }
        }
      })
      .use(AwsS3Multipart, {
        limit: 5,
        companionUrl: apiBase + '/companion'
      })
      .use(Url, {
        companionUrl: apiBase + '/companion',
        host: apiBase + '/companion',
        // companionCookiesRule: 'include'
      })
    },
    uploadDisabled() {
      return !this.video?.title?.length || this.fileCount < 1
    },
    info() {
      return this.uppy.store.state
    }
  },
  methods: {
    ...mapActions('noti', [
      'showSnackbar',
      'error',
      'success'
    ]),
    closeDialog() {
      this.$emit('close')
      this.uppy.cancelAll()
      this.uploading = false
    },
    fileAdded(e) {
      this.isRemote = e.isRemote
      if(e.isRemote) this.remoteUrl = e.remote.body.url
      this.fileCount++;
      this.$nextTick(async () => {
        this.video.duration = await this.getFileDuration()
      })
    },
    fileRemoved() {
      this.isRemote = false
      this.uploading = false
      this.fileCount--;
    },
    async upload() {
      this.track('Video upload started')
      /*
        Some serverside stuff required
        Post the video-object at this point
      */
      let durationInSeconds = await this.getFileDuration()
      const validated = this.validateVideoDuration(durationInSeconds)
      if(validated) {
        this.video.duration = durationInSeconds
        this.uppy.upload()
        this.addUploaded(durationInSeconds)
      } else {
        this.noSpaceDialog = true
      }
    },
    getFileDuration() {
      return new Promise((resolve) => {
        var video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          // If duration is 0 or undefined, use a default value of 5 minutes (300 seconds)
          const duration = video.duration || 300;
          resolve(duration)
        }

        video.onerror = () => {
          // If we can't get the duration, use a default value of 5 minutes
          resolve(300)
        }

        video.src = !this.isRemote ? URL.createObjectURL(this.uppy.getFiles()[0].data) : this.remoteUrl;
      })
    },
    validateVideoDuration(newDurationInSeconds) {
      // If duration is 0 or undefined, use a default value of 5 minutes
      const durationToCheck = newDurationInSeconds || 300;
      const allowedInSeconds = (this.currentTeam.total_hours_saved || 50) * 60 * 60
      const newTotal = this.amountUploaded + durationToCheck
      return newTotal < allowedInSeconds
    },
    uploadStarts() {
      this.uploading = true
    },
    uploaded(payload, body) {
      const fromUrl = payload.source == 'Url'
      const video = {
        ...this.video,
        id: fromUrl ? this.getKeyFromOriginalUrl(body.uploadURL) : payload.s3Multipart?.key,
        s3_key: fromUrl ? '123' : payload.s3Multipart?.key.split('/')[0],
        original_type: fromUrl ? payload.meta.type : payload.data.type,
        original_size: payload.data.size,
        original_url: body.uploadURL,
        team_id: this.currentTeamId
      }

      this.postVideo(video)
        .then(e => {
          this.addVideoAndPoll(e.data.video)
          this.success(this.$t('videos.video_added'))

          console.log(this.video)

          this.track('Added video', {
            ...this.video
          })

          try {
            window.productFruits?.api?.checklists?.markItemAsDone('video-added', true);
          } catch(e) {
            console.log('not found video-added')
          }

          this.$router.push(`/videos/${video.id.split('/')[0]}`)
        })
        .catch(err => this.error(err))
    },
    getKeyFromOriginalUrl(original_url) {
      let arr = original_url.split('/')
      return arr[arr.length - 1]
    },
    complete() {
      // TODO: notification
      this.$emit('close')
      this.uploading = false
    },
    uppyError() {
      this.uploading = false
      this.error( this.$t('dashboard.something_went_wrong') )
    },
    progress() {
      // console.log(e)
    },
    ...mapActions('videos', [
      'postVideo',
      'addVideoAndPoll'
    ]),
    ...mapMutations('videos', [
      'addUploaded'
    ])
  },
  beforeDestroy () {
    this.uppy.close()
  }
}
</script>